import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, card, input, btnPrimary, btnSecondary } from '../styles/theme.js'
import { scenarioPacks as api } from '../lib/api.js'

const breadcrumb = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  margin-bottom: ${space.lg};
  a { color: ${colors.textSecondary}; }
`

const titleRow = css`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${space.xl};
`

const pageTitle = css`
  font-size: ${font.size['2xl']};
  font-weight: ${font.weight.bold};
  line-height: 1.2;
`

const desc = css`
  font-size: ${font.size.base};
  color: ${colors.textSecondary};
  margin-top: ${space.xs};
  max-width: 600px;
`

const sectionTitle = css`
  font-size: ${font.size.lg};
  font-weight: ${font.weight.semibold};
  margin-bottom: ${space.md};
`

const scenarioList = css`
  ${card};
  margin-bottom: ${space.xl};
`

const scenarioRow = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${space.sm} ${space.md};
  border-bottom: 1px solid ${colors.border};
  cursor: pointer;
  &:last-child { border-bottom: none; }
  &:hover { background: ${colors.bg}; }
`

const scenarioContent = css`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${font.size.sm};
`

const scenarioMeta = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  margin-left: ${space.md};
  white-space: nowrap;
`

const detailGrid = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${space.sm};
  margin-bottom: ${space.xl};
`

const detailChip = css`
  ${card};
  padding: ${space.sm} ${space.md};
`

const detailName = css`
  font-weight: ${font.weight.medium};
  font-size: ${font.size.sm};
`

const detailType = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  margin-top: 2px;
`

const emptyState = css`
  text-align: center;
  padding: ${space.lg};
  color: ${colors.textMuted};
  font-size: ${font.size.sm};
`

const actionRow = css`
  display: flex;
  gap: ${space.sm};
  margin-bottom: ${space.md};
`

const formRow = css`
  margin-bottom: ${space.md};
`

const formActions = css`
  display: flex;
  gap: ${space.sm};
  justify-content: flex-end;
  margin-top: ${space.lg};
`

const inlineEdit = css`
  ${input};
  font-size: ${font.size['2xl']};
  font-weight: ${font.weight.bold};
  padding: ${space.xs} ${space.sm};
  width: 100%;
`

const dangerBtn = css`
  ${btnSecondary};
  color: ${colors.critical};
  border-color: ${colors.critical};
  &:hover { background: ${colors.brandLight}; }
`

export function PackView() {
  let projectId, packId
  let pack = null
  let loading = true
  let error = null
  let editing = false
  let editForm = { label: '', description: '' }
  let saving = false

  async function load() {
    loading = true
    error = null
    try {
      pack = await api.get(projectId, packId)
    } catch (e) {
      error = e.message
    }
    loading = false
    m.redraw()
  }

  async function savePack() {
    saving = true
    try {
      await api.update(projectId, packId, {
        label: editForm.label.trim(),
        description: editForm.description.trim() || null,
      })
      editing = false
      await load()
    } catch (e) {
      error = e.message
    }
    saving = false
    m.redraw()
  }

  async function deletePack() {
    if (!confirm('Delete this pack and all its scenarios?')) return
    try {
      await api.delete(projectId, packId)
      m.route.set(`/projects/${projectId}/scenarios`)
    } catch (e) {
      error = e.message
      m.redraw()
    }
  }

  async function createAndOpenScenario() {
    if (saving) return
    saving = true
    try {
      const scenario = await api.createScenario(projectId, packId, {
        content: '(new scenario)',
      })
      m.route.set(`/projects/${projectId}/scenarios/${packId}/${scenario.id}/scenario`)
    } catch (e) {
      error = e.message
      saving = false
      m.redraw()
    }
  }

  async function deleteScenario(scenarioId, e) {
    e.stopPropagation()
    if (!confirm('Delete this scenario?')) return
    try {
      await api.deleteScenario(projectId, packId, scenarioId)
      await load()
    } catch (e) {
      error = e.message
      m.redraw()
    }
  }

  function getScenarioDetailCount(scenarioId) {
    if (!pack || !pack.scenarioDetails) return 0
    return pack.scenarioDetails.filter(sd => sd.scenario_id === scenarioId).length
  }

  function getScenarioRubricStatus(scenarioId) {
    // We don't have rubric data in the pack response — just show detail count
    return ''
  }

  return {
    oninit(vnode) {
      projectId = vnode.attrs.projectId
      packId = vnode.attrs.packId
      load()
    },
    view() {
      if (loading) return m('div', { class: emptyState }, 'Loading...')
      if (!pack) return m('div', { class: emptyState }, error || 'Pack not found')

      return m('div', [
        m('div', { class: breadcrumb }, [
          m(m.route.Link, { href: '/projects' }, 'Projects'),
          ' / ',
          m(m.route.Link, { href: `/projects/${projectId}` }, 'Project'),
          ' / ',
          m(m.route.Link, { href: `/projects/${projectId}/scenarios` }, 'Scenarios'),
          ` / ${pack.label}`,
        ]),

        error ? m('div', {
          style: `color: ${colors.critical}; margin-bottom: ${space.md};`,
        }, error) : null,

        // Header
        editing
          ? m('div', { style: `margin-bottom: ${space.xl};` }, [
              m('input', {
                class: inlineEdit,
                value: editForm.label,
                oninput: (e) => { editForm.label = e.target.value },
              }),
              m('textarea', {
                class: `${input}; margin-top: ${space.sm};`,
                rows: 2,
                value: editForm.description,
                oninput: (e) => { editForm.description = e.target.value },
                placeholder: 'Description',
              }),
              m('div', { class: formActions }, [
                m('button', {
                  class: btnSecondary,
                  onclick: () => { editing = false },
                }, 'Cancel'),
                m('button', {
                  class: btnPrimary,
                  onclick: savePack,
                  disabled: saving,
                }, 'Save'),
              ]),
            ])
          : m('div', { class: titleRow }, [
              m('div', [
                m('h1', { class: pageTitle }, pack.label),
                pack.description ? m('p', { class: desc }, pack.description) : null,
              ]),
              m('div', { style: `display: flex; gap: ${space.sm};` }, [
                m('button', {
                  class: btnSecondary,
                  onclick: () => {
                    editing = true
                    editForm = { label: pack.label, description: pack.description || '' }
                  },
                }, 'Edit'),
                m('button', {
                  class: dangerBtn,
                  onclick: deletePack,
                }, 'Delete'),
              ]),
            ]),

        // Scenarios section
        m('div', { class: actionRow }, [
          m('h2', { class: sectionTitle, style: 'margin-bottom: 0;' }, 'Scenarios'),
          m('button', {
            class: btnPrimary,
            style: `margin-left: auto; font-size: ${font.size.sm}; padding: ${space.xs} ${space.md};`,
            onclick: createAndOpenScenario,
            disabled: saving,
          }, saving ? 'Creating...' : 'Add Scenario'),
        ]),

        pack.scenarios && pack.scenarios.length > 0
          ? m('div', { class: scenarioList },
              pack.scenarios.map(s =>
                m('div', {
                  class: scenarioRow,
                  key: s.id,
                  onclick: () => m.route.set(`/projects/${projectId}/scenarios/${packId}/${s.id}/scenario`),
                }, [
                  m('div', { class: scenarioContent }, s.content),
                  m('div', { class: scenarioMeta }, [
                    `${getScenarioDetailCount(s.id)} details · ${s.generationCount || 0} generations`,
                  ]),
                  m('button', {
                    class: btnSecondary,
                    style: `font-size: ${font.size.xs}; padding: 2px 8px; margin-left: ${space.sm};`,
                    onclick: (e) => deleteScenario(s.id, e),
                  }, 'Delete'),
                ])
              )
            )
          : m('div', { class: emptyState }, 'No scenarios yet. Add one to get started.'),

        // Detail pool overview
        m('h2', { class: sectionTitle, style: `margin-top: ${space.xl};` }, 'Detail Pool'),
        pack.details && pack.details.length > 0
          ? m('div', { class: detailGrid },
              pack.details.map(d =>
                m('div', { class: detailChip, key: d.id }, [
                  m('div', { class: detailName }, d.name),
                  m('div', { class: detailType }, [
                    d.type === 'enum' ? 'list' : d.type,
                    d.type === 'enum' && d.options && d.options.length > 0
                      ? `: ${d.options.map(o => o.label || o).join(', ')}`
                      : d.type === 'range'
                        ? `: ${d.range_min}–${d.range_max}`
                        : '',
                  ]),
                ])
              )
            )
          : m('div', { class: emptyState }, 'No details defined yet. Add details from within a scenario.'),

      ])
    },
  }
}
